import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { taskCategoriesApi } from "@/features/tasks/api/categories";
import { taskKeys } from "@/features/tasks/hooks/useTasks";
import type { TaskCategory } from "@/features/tasks/types";

export const categoryKeys = {
  all: ["task-categories"] as const,
  list: () => [...categoryKeys.all, "list"] as const,
};

export function useTaskCategories() {
  return useQuery({
    queryKey: categoryKeys.list(),
    queryFn: () => taskCategoriesApi.list(),
    staleTime: 30_000,
  });
}

export function useCreateTaskCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { name: string; color: string }) => taskCategoriesApi.create(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: categoryKeys.all });
    },
  });
}

export function useUpdateTaskCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...data }: { id: string; name?: string; color?: string }) =>
      taskCategoriesApi.update(id, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: categoryKeys.all });
      qc.invalidateQueries({ queryKey: taskKeys.all });
    },
  });
}

export function useDeleteTaskCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => taskCategoriesApi.delete(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: categoryKeys.all });
      qc.invalidateQueries({ queryKey: taskKeys.all });
    },
  });
}

export function useSetTaskCategory(taskId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (categoryId: string | null) =>
      taskCategoriesApi.setTaskCategory(taskId, categoryId),
    onSuccess: (data) => {
      qc.setQueriesData<TaskCategory[]>({ queryKey: taskKeys.all }, (old) => {
        if (!old || !Array.isArray(old)) return old;
        return old.map((t) =>
          t.id === taskId ? { ...t, my_category: data.my_category } : t,
        );
      });
      qc.invalidateQueries({ queryKey: taskKeys.detail(taskId) });
    },
  });
}
